import { useRef, useCallback, useEffect, useMemo, useState } from 'react'
import { SuperDocEditor as SDEditor } from '@superdoc-dev/react'
import '@superdoc-dev/react/style.css'
import type { SuperDocRef, SuperDocReadyEvent } from '@superdoc-dev/react'
import type { CVDocument } from '../../stores/cvGenerationStore'

interface SuperDocEditorProps {
  document: CVDocument
  initialHtml?: string
  initialDocx?: Blob
  onUpdate?: () => void
  onReady?: () => void
}

/**
 * Convert a CVDocument JSON structure to an HTML string suitable for
 * SuperDoc's HTML-mode initialisation.
 */
export function cvDocumentToHtml(doc: CVDocument): string {
  const { user, sections } = doc
  const parts: string[] = []

  // Header
  parts.push('<div style="text-align:center;margin-bottom:24px;border-bottom:2px solid #C45911;padding-bottom:8px">')
  parts.push(`<h1 style="font-size:14pt;font-weight:300;letter-spacing:3px;color:#833C0B;text-transform:uppercase">${esc(user.name)}</h1>`)
  const contactParts: string[] = []
  if (user.email) contactParts.push(user.email)
  if (user.linkedin_url) contactParts.push(user.linkedin_url.replace(/^https?:\/\//, ''))
  if (user.phone) contactParts.push(user.phone)
  if (contactParts.length) {
    parts.push(`<p style="font-size:9pt;color:#545f6e">${contactParts.map(esc).join(' | ')}</p>`)
  }
  parts.push('</div>')

  for (const section of sections) {
    // Skip sections that have no content to render
    if (section.type === 'EDUCATION' && (user.education ?? []).length === 0) continue
    if (section.type === 'LANGUAGES' && (user.languages ?? []).length === 0) continue

    const content = section.content as Record<string, unknown>
    const label = ({
      SUMMARY: 'ABOUT ME',
      EXPERIENCE: 'PROFESSIONAL EXPERIENCE',
      SKILLS: 'CORE COMPETENCIES',
      EDUCATION: 'EDUCATION',
      LANGUAGES: 'LANGUAGES',
    } as Record<string, string>)[section.type] || section.type

    parts.push(`<h2 style="text-align:center;font-size:9pt;font-weight:300;letter-spacing:2px;color:#833C0B;text-transform:uppercase;border-bottom:1px solid #823B0B;padding-bottom:4px;margin-bottom:8px">${esc(label)}</h2>`)

    if (section.type === 'SUMMARY') {
      parts.push(`<p style="font-size:10pt">${esc(String(content.text ?? ''))}</p>`)
    }

    if (section.type === 'EXPERIENCE') {
      const roles = (content.roles as Array<Record<string, unknown>>) ?? []
      for (const role of roles) {
        parts.push('<div style="margin-bottom:12px">')
        parts.push(`<p style="font-size:10pt"><strong>${esc(String(role.title))}</strong>, ${esc(String(role.employer))}<span style="float:right;color:#545f6e;font-size:9pt">${esc(String(role.period))}</span></p>`)
        const projects = (role.projects as Array<Record<string, unknown>>) ?? []
        for (const proj of projects) {
          if (proj.name) {
            parts.push(`<p style="font-size:10pt"><strong>${esc(String(proj.name))}</strong>`)
            if (proj.description) parts.push(` – ${esc(String(proj.description))}`)
            parts.push('</p>')
          }
          const bullets = (proj.bullets as string[]) ?? []
          if (bullets.length) {
            parts.push('<ul>')
            for (const b of bullets) {
              parts.push(`<li style="font-size:10pt">${esc(b)}</li>`)
            }
            parts.push('</ul>')
          }
        }
        parts.push('</div>')
      }
    }

    if (section.type === 'SKILLS') {
      const categories = (content.categories as Array<Record<string, unknown>>) ?? []
      for (const cat of categories) {
        const skills = (cat.skills as string[]) ?? []
        parts.push(`<p style="font-size:10pt"><strong>${esc(String(cat.label))}:</strong> ${skills.map(esc).join(' • ')}</p>`)
      }
    }

    if (section.type === 'EDUCATION') {
      for (const edu of user.education ?? []) {
        parts.push(`<p style="font-size:10pt"><strong>${esc(edu.degree ?? '')}</strong>, ${esc(edu.institution)}`)
        if (edu.year) parts.push(`<span style="float:right;color:#545f6e;font-size:9pt">${esc(edu.year)}</span>`)
        parts.push('</p>')
      }
    }

    if (section.type === 'LANGUAGES') {
      parts.push(`<p style="font-size:10pt">${(user.languages ?? []).map(esc).join(' • ')}</p>`)
    }
  }

  return parts.join('\n')
}

function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Wraps SuperDoc editor component. Initialises from CVDocument JSON via HTML
 * and exposes the SuperDoc instance via ref for content retrieval.
 *
 * SuperDoc only — hard rule #8.
 */
export default function SuperDocEditorWrapper({
  document: cvDoc,
  initialHtml,
  initialDocx,
  onUpdate,
  onReady: onReadyProp,
}: SuperDocEditorProps) {
  const ref = useRef<SuperDocRef>(null)
  const [ready, setReady] = useState(false)

  const html = useMemo(
    () => initialHtml ?? cvDocumentToHtml(cvDoc),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // Initialise once — editor owns content after mount
  )

  // Trim the toolbar to CV-relevant tools (B-10). Keep all text formatting,
  // lists, links, undo/redo, and the LTR/RTL direction toggle; drop tools that
  // make no sense for a CV — image, tables, comments, track-changes, zoom, and
  // the document-mode switch. Names are SuperDoc toolbar item names; comments
  // are disabled via the dedicated module flag.
  const editorModules = useMemo(
    () => ({
      comments: false as const,
      toolbar: {
        excludeItems: [
          'image',
          'table',
          'tableActions',
          'toggleTrackChanges',
          'toggleTrackChangesShowFinal',
          'toggleTrackChangesShowOriginal',
          'acceptTrackedChangeBySelection',
          'rejectTrackedChangeOnSelection',
          'zoom',
          'documentMode',
        ],
      },
    }),
    [],
  )

  const handleReady = useCallback(
    (_event: SuperDocReadyEvent) => { // eslint-disable-line @typescript-eslint/no-unused-vars
      setReady(true)
      onReadyProp?.()
    },
    [onReadyProp],
  )

  useEffect(() => {
    // Store ref on the wrapper element so ReviewStep can access the instance
    const el = document.getElementById('superdoc-editor-root')
    if (el && ref.current) {
      ;(el as HTMLElement & { __superdocRef: SuperDocRef }).__superdocRef = ref.current
    }
    // Enable native browser spellcheck on the editable surface (B-08). SuperDoc
    // exposes no spellcheck config option, so set the attribute directly on the
    // contenteditable node(s) once the editor has mounted.
    if (el && ready) {
      el.querySelectorAll('[contenteditable="true"]').forEach((node) => {
        node.setAttribute('spellcheck', 'true')
      })
    }
  }, [ready])

  return (
    <div id="superdoc-editor-root" className="min-h-[60vh]" dir="auto">
      {initialDocx ? (
        // Docx mode — load from branded binary; preserves all brand OOXML
        <SDEditor
          ref={ref}
          documentMode="editing"
          document={initialDocx}
          disableContextMenu
          onReady={handleReady}
          onEditorUpdate={onUpdate ? () => onUpdate() : undefined}
          hideToolbar={false}
          modules={editorModules}
          className="superdoc-cv-editor"
        />
      ) : (
        // HTML fallback — used when preview-docx fetch failed
        <SDEditor
          ref={ref}
          documentMode="editing"
          html={html}
          disableContextMenu
          onReady={handleReady}
          onEditorUpdate={onUpdate ? () => onUpdate() : undefined}
          hideToolbar={false}
          modules={editorModules}
          className="superdoc-cv-editor"
        />
      )}
    </div>
  )
}

/**
 * Retrieve HTML from the mounted SuperDoc instance.
 * Returns the first editor's HTML or null if not ready.
 */
export function getSuperDocHtml(): string | null {
  const el = document.getElementById('superdoc-editor-root') as
    | (HTMLElement & { __superdocRef?: SuperDocRef })
    | null
  if (!el?.__superdocRef) return null
  const instance = el.__superdocRef.getInstance()
  if (!instance) return null
  const htmlArr = instance.getHTML()
  return htmlArr.length > 0 ? htmlArr[0] : null
}

/**
 * Export the document as a DOCX Blob from SuperDoc.
 */
export async function exportSuperDocDocx(): Promise<Blob | null> {
  const el = document.getElementById('superdoc-editor-root') as
    | (HTMLElement & { __superdocRef?: SuperDocRef })
    | null
  if (!el?.__superdocRef) return null
  const instance = el.__superdocRef.getInstance()
  if (!instance) return null
  const result = await instance.export({ exportType: ['docx'], triggerDownload: false })
  return result instanceof Blob ? result : null
}
